/**
 * Configuration for user identification across different applications
 * Each app can have its own API endpoint and data transformation logic
 */

import {
	getAppIdentPattern,
} from './urlPatterns';

export interface AppIdentConfig {
	/**
	 * The API endpoint to fetch user data
	 */
	apiUrl?: string;

	/**
	 * Transform the raw API response to extract user data
	 * @param response The raw response from the API
	 * @returns The extracted user data
	 */
	extractUserData?: (response: any) => any;

	/**
	 * Validate that the user data contains required fields
	 * @param userData The user data to validate
	 * @returns true if the data is valid for identification
	 */
	validateUserData?: (userData: any) => boolean;

	/**
	 * Extract organization from user data
	 * @param userData The user data to extract organization from
	 * @returns The organization code or undefined
	 */
	extractOrganization: (userData: any) => string | undefined;
}

/**
 * Configuration for each application
 * The 'default' config is used as fallback for unknown apps
 */
export const APP_IDENT_CONFIGS: Record<string, AppIdentConfig> = {
	// AbylaWeb returns user data wrapped in a "user" object
	// Format: {"user": {"id": "...", "organization": "HHA", ...}}
	'abylaweb': {
		apiUrl: getAppIdentPattern('ABYLAWEB'),
		extractOrganization: (userData) => userData?.organization,
		extractUserData: (response) => response?.user || response,
		validateUserData: (userData) => !!(userData?.id),
	},

	// Default configuration for most apps
	// Format: {"uid": "...", "username": "...", ...}
	'default': {
		apiUrl: getAppIdentPattern('DEFAULT'),
		extractOrganization: (userData) => {
			// Try authorities format first (newer format), then direct organizations (legacy)
			const orgs = userData?.authorities?.[0]?.organizations || userData?.organizations;
			if (!orgs || orgs.length === 0) return undefined;
			if (orgs.length === 1) return orgs[0].code;
			return 'STONALINT';
		},
		extractUserData: (response) => response,
		validateUserData: (userData) => !!(userData?.uid || userData?.id),
	},

	// Keep returns flat user data with authorities
	// Format: {"id": "3", "authorities": [{"organizations": [...]}], ...}
	'keep': {
		apiUrl: getAppIdentPattern('KEEP'),
		extractOrganization: (userData) => {
			const orgs = userData?.authorities?.[0]?.organizations;
			if (!orgs || orgs.length === 0) return undefined;
			if (orgs.length === 1) return orgs[0].code;
			return 'STONALINT';
		},
		extractUserData: (response) => response,
		validateUserData: (userData) => !!(userData?.id),
	},

	// Model uses AbylaWeb's API structure
	// Format: {"user": {"id": "...", "organization": "...", ...}}
	'model': {
		apiUrl: getAppIdentPattern('MODEL'),
		extractOrganization: (userData) => userData?.organization,
		extractUserData: (response) => response?.user || response,
		validateUserData: (userData) => !!(userData?.id),
	},
};

/**
 * Get the configuration for a specific app
 * @param appName The name of the application
 * @returns The configuration for the app, or the default config if not found
 */
export function getAppIdentConfig(appName: string | undefined): AppIdentConfig {
	if (!appName) {
		return APP_IDENT_CONFIGS['default'];
	}

	return APP_IDENT_CONFIGS[appName] || APP_IDENT_CONFIGS['default'];
}
